/* import __COLOCATED_TEMPLATE__ from './form.hbs'; */
import { action } from '@ember/object';
import { bannerError } from 'client-portal/utils/error-handling';
import { formatPhoneNumber } from 'client-portal/utils/format-phone-number';
import { gte, or, reads } from 'macro-decorators';
import { scrollToValidationError } from 'client-portal/utils/validate-record';
import { service } from '@ember/service';
import { success } from 'client-portal/utils/banners';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import ENV from 'client-portal/config/environment';
import stateGroups from 'ember-simplepractice/utils/state-groups';
import styles from './form.module.scss';

const insuredDetailFields = [
  'insuredFname',
  'insuredMname',
  'insuredLname',
  'insuredDob',
  'insuredPhone',
  'insuredAddress',
  'insuredCity',
  'insuredState',
  'insuredZip',
];

export default class SiteBillingInsuranceForm extends Component {
  @service analytics;
  @service intl;
  @service mixpanel;
  @service router;
  @service store;

  styles = styles;
  stateGroups = stateGroups;

  @tracked isLoading = false;
  @tracked isReview = false;
  @tracked previousInsuredRelation = null;

  @reads('args.client') client;
  @reads('args.insuranceInfoRequestConnection') insuranceInfoRequestConnection;
  @reads('args.insuranceInfo') insuranceInfo;
  @or('insuranceInfoRequestConnection', 'insuranceInfo') insuranceInfoObject;
  @reads('args.displayOnly') displayOnly;
  @gte('insuranceCards.length', 1) showInsuranceCardBack;

  constructor() {
    super(...arguments);

    if (this.args.displayOnly) {
      this.isReview = true;
    }
  }

  get sexOptions() {
    return ['Female', 'Male'];
  }

  get insuredRelations() {
    return {
      '0': this.intl.t('billing.insurance.relations.self'),
      '1': this.intl.t('billing.insurance.relations.spouse'),
      '2': this.intl.t('billing.insurance.relations.parent'),
      '3': this.intl.t('billing.insurance.relations.other'),
    };
  }

  get insuredRelationText() {
    return (
      this.insuredRelations[this.insuranceInfoObject?.insuredRelation] ||
      this.intl.t('billing.insurance.relations.self')
    );
  }

  get showInsuredDetails() {
    return this.insuranceInfoObject.insuredRelation !== '0';
  }

  get insuranceCards() {
    return (
      this.insuranceInfoObject?.documents?.filter(
        doc => doc.constructor.modelName === 'insurance-card'
      ) || []
    );
  }

  get insuranceCardFront() {
    return this.insuranceCards.slice().at(0);
  }

  get insuranceCardBack() {
    return this.insuranceCards.slice().at(1);
  }

  get pageTitle() {
    if (this.isReview && this.insuranceInfoObject.insurancePlan) {
      return this.insuranceInfoObject.insurancePlan.nameWithPayer;
    }
    if (this.isReview) {
      return this.intl.t('billing.documents.insurance');
    } else {
      return this.intl.t('billing.documents.insuranceInfo');
    }
  }

  get hasInsuredAddress() {
    return !!(
      this.insuranceInfoObject.insuredAddress ||
      this.insuranceInfoObject.insuredCity ||
      this.insuranceInfoObject.insuredState ||
      this.insuranceInfoObject.insuredZip
    );
  }

  @action
  goBackToForm() {
    this.isReview = false;
  }

  @action
  async handleInsuredRelationChange(value) {
    this.insuranceInfoObject.set('insuredRelation', value);

    if (this.insuranceInfoObject.selfInsured) {
      this.clearInsuredDetails();
    }
  }

  @action
  clearInsuredDetails() {
    insuredDetailFields.forEach(field => {
      this.insuranceInfoObject[field] = null;
    });
  }

  @action
  async handleSubmit() {
    if (!this.isReview) {
      if (this.insuranceInfoObject.validations.isInvalid) {
        await waitForRender();
        this.insuranceInfoObject.set('isDirty', true);
        scrollToValidationError();
        return;
      }
      this.isReview = true;
    } else {
      try {
        this.isLoading = true;
        await this.saveInsuranceTask.perform();

        // Reload to show new pending request after submitting
        try {
          await this.store.query('clinician-insurance-info-review', {
            include:
              'client,insuranceInfoRequestConnection,insuranceInfoRequestConnection.insurancePlan',
            page: { size: 50 },
          });
        } catch (error) {
          bannerError('Failed to reload insurance information.');
        }

        this.args.setSubmitted();
        this.analytics.track('Client Insurance Info Submission Completed', {
          object: 'Client Insurance Info Submission',
          action: 'Completed',
        });

        success({ title: this.intl.t('billing.insurance.success.insuranceUpdated') });
        this.router.transitionTo('site.billing.insurance');
      } catch (error) {
        bannerError('Failed to save insurance information. Please try again.');
      } finally {
        this.isLoading = false;
      }
    }
  }

  @task
  *saveInsuranceTask() {
    this.insuranceInfoObject.set('thisType', 'other');

    // Format phone number
    if (this.insuranceInfoObject.insuredPhone) {
      this.insuranceInfoObject.set(
        'insuredPhone',
        formatPhoneNumber(this.insuranceInfoObject.insuredPhone)
      );
    }

    yield this.insuranceInfoObject.save();
    yield this._saveInsuranceCards();
    yield this._saveInsuranceTextExtractions();
  }

  @(task(function* (search) {
    return this.store.query('insurance-provider-name', {
      filter: {
        search,
      },
      page: {
        size: ENV.maxPageSize,
      },
    });
  }).restartable())
  searchInsuranceProviderNamesTask;

  async _saveInsuranceCards() {
    let promiseArray = [];
    this.insuranceInfoObject.documents.forEach(card => {
      if (card.isMarkedForDeletion) {
        promiseArray.push(card.destroyRecord());
      } else if (card.isNew) {
        promiseArray.push(card.save());
      }
    });

    return await Promise.all(promiseArray);
  }

  async _saveInsuranceTextExtractions() {
    let promiseArray = [];
    this.insuranceInfoObject.documents.forEach(card => {
      if (card.insuranceTextExtractionDraftId) {
        let insuranceTextExtractionDraft = this.store.peekRecord(
          'insurance-text-extraction',
          card.insuranceTextExtractionDraftId
        );
        let insuranceTextExtraction = this.store.createRecord('insurance-text-extraction', {
          insuranceCard: card,
          memberId: insuranceTextExtractionDraft.memberId,
          memberIdConfidenceScore: insuranceTextExtractionDraft.memberIdConfidenceScore,
          groupId: insuranceTextExtractionDraft.groupId,
          groupIdConfidenceScore: insuranceTextExtractionDraft.groupIdConfidenceScore,
          payerName: insuranceTextExtractionDraft.payerName,
          payerNameConfidenceScore: insuranceTextExtractionDraft.payerNameConfidenceScore,
          payerPhone: insuranceTextExtractionDraft.payerPhone,
          payerPhoneConfidenceScore: insuranceTextExtractionDraft.payerPhoneConfidenceScore,
          planId: insuranceTextExtractionDraft.planId,
          planIdConfidenceScore: insuranceTextExtractionDraft.planIdConfidenceScore,
          payerId: insuranceTextExtractionDraft.payerId,
          payerIdConfidenceScore: insuranceTextExtractionDraft.payerIdConfidenceScore,
          claimAddress: insuranceTextExtractionDraft.claimAddress,
          claimAddressConfidenceScore: insuranceTextExtractionDraft.claimAddressConfidenceScore,
        });

        promiseArray.push(insuranceTextExtraction.save());
      }
    });
    return await Promise.all(promiseArray);
  }
}
